import { SnackbarEvents } from "@gtmhub/shared/components/ui/snackbar/snackbar.events";
import { IScope, IRootScopeService, IPromise, IQService } from "angular";
import { StateService } from "@uirouter/angularjs";
import { IModalScope } from "angular-ui-bootstrap";
import { UserProfileService } from "./profileService";
import { IProfile } from "@webapp/users/models/users.models";
import { CurrentUserService } from "../currentUserService";
import { UIErrorHandlingService } from "@gtmhub/error-handling";
import { getAuth0UserId } from "@gtmhub/auth";
import { IUserMetadata, ISendChangePasswordRequestRequest } from "../../login/models";
import { IUser, IPatchedUser } from "../models";
import { UserService } from "../userService";
import { ILanguageMap, filterLanguages } from "@gtmhub/localization";
import { IStateInit } from "@gtmhub/core/routing";
import { getCurrentUserId } from "../current-user-storage";
import { CurrentUserPictureService } from "../current-user-picture-service";
import { EmailVerificationComponent } from "@gtmhub/edition-plan-change";
import { LanguagesConfigService } from "@gtmhub/localization/services/languages-config.service";
import languages from "wwwroot/json/languages.json";

export interface IAvatarScope extends IModalScope {
  indicators: {
    loading?: boolean;
    uploading?: boolean;
  };
  picFile?: Blob;
  profile: IProfile;
  croppedDataUrl?: string;
  uploadedFileUrl?: string;
  upload(): void;
  remove(): void;
  restore(): void;
}

export class AvatarCtrl {
  private snackbarEvents: SnackbarEvents;
  public static $inject = ["$scope", "$state", "CurrentUserPictureService", "UserProfileService", "UserService", "UIErrorHandlingService"];

  constructor(
    private $scope: IAvatarScope,
    private $state: StateService,
    private currentUserPictureService: CurrentUserPictureService,
    private profileService: UserProfileService,
    private userService: UserService,
    private uiErrorHandlingService: UIErrorHandlingService
  ) {
    this.snackbarEvents = new SnackbarEvents($scope);
    this.$scope.upload = this.upload;
    this.$scope.remove = this.remove;
    this.$scope.restore = this.restore;
    this.$scope.indicators = {};
    this.$scope.profile = this.profileService.getProfile();
  }

  private restore = () => {
    const patchedUser: IPatchedUser = {
      restoreUserPicture: true,
    };

    this.$scope.indicators.uploading = true;

    this.userService
      .patchUser(getCurrentUserId(), patchedUser)
      .then(() => {
        this.userService.getUser(getCurrentUserId()).then((user) => {
          this.$scope.indicators.uploading = false;
          const profile = this.profileService.getProfile();
          profile.picture = user.picture;
          profile.pictureInfo.isPictureEdited = user.pictureInfo.isPictureEdited;
          this.profileService.userProfileUpdated(profile);
          this.$state.go("gtmhub.userProfile");
          this.snackbarEvents.broadcastShowSnackbar("avatar_updated");
        });
      })
      .catch((err) => {
        this.$scope.indicators.uploading = false;
        this.uiErrorHandlingService.handleModal(err);
      });
  };

  private remove = () => {
    this.$scope.picFile = null;
    this.$scope.croppedDataUrl = "";
  };

  private upload = () => {
    if (!this.$scope.croppedDataUrl) {
      return;
    }

    this.$scope.indicators.uploading = true;

    this.currentUserPictureService.updatePicture(this.$scope.croppedDataUrl).then(
      (url) => {
        this.$scope.indicators.uploading = false;
        this.$scope.uploadedFileUrl = url;
        this.$state.go("gtmhub.userProfile");
        this.snackbarEvents.broadcastShowSnackbar("avatar_updated");
      },
      (err) => {
        this.$scope.indicators.uploading = false;
        this.uiErrorHandlingService.handleModal(err);
      }
    );
  };
}

export interface IUserProfileScope extends IScope {
  page: string;
  errorMessage: string;
  auth0UserId: string;
  hasModifications: boolean;
  profile: IProfile;
  indicators;
  type: string;
  typeEmailVerificationComponent: EmailVerificationComponent;
  save(): void;
  trackChange(): void;
}

export class UserProfileCtrl {
  private snackbarEvents: SnackbarEvents;
  public static $inject = ["$rootScope", "$scope", "UserService", "UserProfileService"];

  constructor(private $rootScope: IRootScopeService, private $scope: IUserProfileScope, private userService: UserService, private profileService: UserProfileService) {
    this.snackbarEvents = new SnackbarEvents($scope);
    this.init();
  }

  private init = () => {
    this.$scope.indicators = {};
    this.$scope.errorMessage = "";
    this.$scope.type = "profile";
    this.$scope.typeEmailVerificationComponent = "profile";
    this.$scope.save = this.save;
    this.$scope.trackChange = this.trackChange;
    this.$scope.hasModifications = false;

    this.loadUser();
    this.subscribeForProfileUpdate();
  };

  private trackChange = () => {
    this.$scope.hasModifications = true;
  };

  private save = () => {
    this.$scope.errorMessage = "";
    this.$scope.indicators.saving = true;
    const userMetadata: IUserMetadata = {
      firstName: this.$scope.profile.firstName,
      lastName: this.$scope.profile.lastName,
    };

    this.userService.addUserMetadata(this.$scope.auth0UserId, userMetadata).then(
      () => {
        this.$scope.indicators.saving = false;
        this.$scope.hasModifications = false;
        this.snackbarEvents.broadcastShowSnackbar("profile_updated");
      },
      (error) => {
        this.$scope.errorMessage = error.message;
        this.$scope.indicators.saving = false;
      }
    );
  };

  private loadUser = () => {
    this.$scope.profile = this.profileService.getProfile();
    this.$scope.auth0UserId = getAuth0UserId();
  };

  private subscribeForProfileUpdate = () => {
    const removeUserProfileUpdatedListener = this.$rootScope.$on("userProfileUpdated", (evnt, profile: IProfile) => {
      this.$scope.profile = profile;
    });

    this.$scope.$on("$destroy", removeUserProfileUpdatedListener);
  };
}

export interface ISendChangePasswordRequestScope extends IScope {
  auth0UserId: string;
  profile: IProfile;
  sendRequest(): void;
}

export class SendChangePasswordRequestCtrl {
  private snackbarEvents: SnackbarEvents;
  public static $inject = ["$state", "$scope", "UserService", "UserProfileService", "UIErrorHandlingService"];

  constructor(
    private $state: StateService,
    private $scope: ISendChangePasswordRequestScope,
    private userService: UserService,
    private profileService: UserProfileService,
    private uiErrorHandlingService: UIErrorHandlingService
  ) {
    this.snackbarEvents = new SnackbarEvents($scope);
    this.init();
  }

  private init = () => {
    this.$scope.sendRequest = this.sendRequest;

    this.loadUser();
  };

  private loadUser = () => {
    this.$scope.profile = this.profileService.getProfile();
    this.$scope.auth0UserId = getAuth0UserId();
  };

  private sendRequest = () => {
    const email = this.$scope.profile.email;

    const data: ISendChangePasswordRequestRequest = {
      email: email,
    };

    this.userService.sendChangePasswordRequest(this.$scope.auth0UserId, data).then(
      () => {
        this.$state.go("gtmhub.userProfile");
        this.snackbarEvents.broadcastShowSnackbar("send_change_password_request");
      },
      (error) => {
        this.uiErrorHandlingService.handleModal(error);
      }
    );
  };
}

export interface IUserLanguageScope extends IScope {
  profile: IProfile;
  indicators;
  user: IUser;
  supportedLanguages: ILanguageMap;
  initialLanguage: string;
  setLanguage(language: string): void;
}

export class UserLanguageCtrl implements IStateInit {
  private snackbarEvents: SnackbarEvents;

  public static $inject = ["$rootScope", "$scope", "$q", "CurrentUserService", "UserService", "UserProfileService", "UIErrorHandlingService", "LanguagesConfigService"];

  constructor(
    private $rootScope: IRootScopeService,
    private $scope: IUserLanguageScope,
    private $q: IQService,
    private currentUserService: CurrentUserService,
    private userService: UserService,
    private profileService: UserProfileService,
    private uiErrorHandlingService: UIErrorHandlingService,
    private languagesConfigService: LanguagesConfigService
  ) {
    this.snackbarEvents = new SnackbarEvents($scope);
  }

  public stateInit(): IPromise<unknown> {
    return this.init();
  }

  private init = (): IPromise<unknown> => {
    this.$scope.indicators = {};
    this.$scope.setLanguage = this.setLanguage;

    this.loadUser();
    this.subscribeForProfileUpdate();

    this.$scope.indicators.user = true;
    return this.$q.all([this.getUser(), this.loadSupportedLanguages()]).then(() => (this.$scope.indicators.user = false));
  };

  private loadUser = () => {
    this.$scope.profile = this.profileService.getProfile();
  };

  private getUser = (): IPromise<unknown> => {
    return this.userService.getUser(getCurrentUserId()).then((user) => {
      this.$scope.user = user;
      this.setInitialLanguage(user.language);
    });
  };

  private loadSupportedLanguages = (): IPromise<ILanguageMap> => {
    return this.languagesConfigService
      .getLanguageMap()
      .then((languageMap) => (this.$scope.supportedLanguages = languageMap))
      .catch(() => (this.$scope.supportedLanguages = filterLanguages("italian", languages.languageMap)));
  };

  private setInitialLanguage = (language: string) => {
    this.$scope.initialLanguage = language;
  };

  // eslint-disable-next-line sonarjs/no-identical-functions
  private subscribeForProfileUpdate = () => {
    const removeUserProfileUpdatedListener = this.$rootScope.$on("userProfileUpdated", (evnt, profile: IProfile) => {
      this.$scope.profile = profile;
    });

    this.$scope.$on("$destroy", removeUserProfileUpdatedListener);
  };

  private setLanguage = (language: string) => {
    this.$scope.indicators.saving = true;

    const userSettings = {
      language: language,
    };

    this.userService.updateUserSettings(this.$scope.user.id, userSettings).then(
      () => {
        this.$scope.indicators.saving = false;
        this.currentUserService.raiseProfileLanguageUpdated(language);
        this.currentUserService.setUserLanguage(language);
        this.setInitialLanguage(userSettings.language);
        this.snackbarEvents.broadcastShowSnackbar("language_updated");
        location.reload();
      },
      (error) => this.uiErrorHandlingService.handleIndicator(error, { indicator: "saving", inline: true })
    );
  };
}
